import React, { forwardRef, useState } from 'react';
import {
  Divider,
  Drawer as MuiDrawer,
  StyledEngineProvider,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Typography } from '../index';
import * as CommonIcons from 'assets/common';
import { Box } from '@mui/material';
import {
  classNames,
  getDataPropsFromRest,
  isNullOrUndefined,
} from 'utilities/Utils';

// Type defination for Drawer.tsx

export type DrawerProps = {
  id?: string;
  className?: string;
  isOpen: boolean;
  title?: string;
  onClose?:
  | ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void)
  | undefined;
  onCrossCancel?:
  | ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void)
  | undefined;
  onOutsideClick?:
  | ((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)
  | undefined;
  enableOutsideClick?: boolean;
  subTitle?: React.ReactNode;
  onConfirm?:
  | ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void)
  | undefined;
  negativeText?: string;
  positiveText?: string;
  hideFooter?: boolean;
  disablePositive?: boolean;
  leftChildren?: React.ReactNode;
  children: React.ReactNode | string | HTMLElement | React.ReactNode[];
  headerLeftElem?: React.ReactNode;
  hideCross?: boolean;
  rootClassName?: string;
  onSecondaryAction?:
  | ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void)
  | undefined;
  displayLeftChildren?: boolean;
  drawerChildrenClassName?: string;
  drawerContainerClassName?: string;
  showDivider?: boolean;
  bannerHeight?: number;
  headerDrawerClassName?: string;
  footerClass?: string;
};
//end of type definition

const useStyles = makeStyles(() => ({
  root: {
    '&.MuiDrawer-root': {
      position: 'revert !important',
    },
  },
  zIndex: {
    zIndex: '1000',
  },
  footer: {
    padding: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    '& .btn-cancel': {
      marginRight: '20px',
    },
  },
  negativeCTA: {
    marginRight: 20,
  },
  backdrop: {
    backgroundColor: 'rgba(49, 49, 49, 0.5)',
  },
  divider: {
    marginTop: '16px',
    marginLeft: '-20px',
    marginRight: '-20px',
  },
  drawerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: '20px',
  },
  drawerTitle: {
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '27px',
    color: '#404040',
  },
  drawerSubTitle: {
    paddingRight: '20px',
  },
  drawerHeaderLeftElem: {
    display: 'flex',
    marginRight: '16px',
    alignItems: 'center',
  },
  drawerChidrenContent: {
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '21px',
    fontStyle: 'normal',
    color: '#4A4A4A',
    marginTop: '16px',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: '20px',
  },
  drawerContainer: {
    width: '424px',
    height: '100%',
    padding: '16px',
    overflow: 'hidden',
    paddingBottom: '75px',
    paddingRight: '0px',
  },
  drawerLeftMove: {
    marginLeft: '40px',
  },
  //Need to relook at this after navigation project goes live
  leftChildContainer: {
    position: 'fixed',
    width: 'calc(100vw - 424px)',
    height: '100vh',
    top: '0',
    left: '0',
    backgroundColor: 'rgb(95,95,95,0.4)',
  },
  bannerStyle: (props: { bannerHeight: number }) => ({
    height: `calc(100% - ${props.bannerHeight}px)`,
    top: `${props.bannerHeight}px`,
  }),
}));

function Drawer(props: DrawerProps, ref?: React.Ref<HTMLInputElement>) {
  const {
    children,
    isOpen,
    title,
    subTitle,
    onClose,
    onConfirm,
    onOutsideClick,
    onCrossCancel,
    className,
    onSecondaryAction,
    negativeText = 'cancel',
    positiveText = 'Submit',
    hideFooter = false,
    disablePositive = false,
    leftChildren = null,
    headerLeftElem,
    hideCross = false,
    showDivider = true,
    rootClassName = '', // TODO: remove root classname after z-index config
    displayLeftChildren = false,
    drawerChildrenClassName,
    drawerContainerClassName,
    id,
    bannerHeight = 0,
    headerDrawerClassName,
    footerClass = '',
    ...rest
  } = props;
  const classes = useStyles({ bannerHeight });

  const [showLeftChildren, setShowLeftChildren] = useState(displayLeftChildren);
  const dataTestIdProps = getDataPropsFromRest(rest);

  const containerClass = className
    ? `aa-drawer-container ${className} }`
    : 'aa-drawer-container';

  const internalOnClose = (ev, reason) => {
    if (!isNullOrUndefined(leftChildren) && reason === 'backdropClick') {
      return;
    }
    if (onOutsideClick) {
      return onOutsideClick(ev);
    }
    if (onClose) {
      return onClose(ev);
    }
  };

  const updateView = () => {
    if (leftChildren) {
      setShowLeftChildren(true);
    }
  };

  return (
    <StyledEngineProvider injectFirst>
      <MuiDrawer
        id={id}
        anchor="right"
        onClose={internalOnClose}
        classes={{
          paper: classNames(containerClass, classes.bannerStyle),
          root: rootClassName,
        }}
        ModalProps={{ BackdropProps: { className: classes.backdrop } }}
        SlideProps={{ onEntered: updateView }}
        open={isOpen}
        className={
          leftChildren
            ? classNames(classes.root, classes.zIndex)
            : classes.zIndex
        }
        {...dataTestIdProps}
      >
        <div
          className={
            drawerContainerClassName
              ? classNames(classes.drawerContainer, drawerContainerClassName)
              : classes.drawerContainer
          }
          ref={ref}
        >
          {title && (
            <Box>
              <Box
                className={classNames(
                  classes.drawerContent,
                  headerDrawerClassName,
                )}
              >
                <Box className={classes.drawerHeaderLeftElem}>
                  {headerLeftElem && (
                    <Box className={classes.drawerHeaderLeftElem}>
                      {headerLeftElem}
                    </Box>
                  )}
                  <Box className={classes.drawerTitle}>{title}</Box>
                </Box>
                {!hideCross && (
                  <Box
                    onClick={onCrossCancel ?? onClose}
                    style={{ cursor: 'pointer' }}
                  >
                    <CommonIcons.Close />
                  </Box>
                )}
              </Box>
              {subTitle && headerLeftElem ? (
                <Box
                  className={`${classes.drawerSubTitle} ${classes.drawerLeftMove}`}
                >
                  <Typography
                    variant="subtitle1"
                    overflowWrap
                    themeColor="gray.light"
                  >
                    {subTitle}
                  </Typography>
                </Box>
              ) : (
                <Box className={classes.drawerSubTitle}>
                  <Typography variant="body1" overflowWrap>
                    {subTitle}
                  </Typography>
                </Box>
              )}
              {showDivider && (
                <Divider color="#DEDEDE" className={classes.divider} />
              )}
            </Box>
          )}
          <Box
            className={classNames(
              classes.drawerChidrenContent,
              drawerChildrenClassName,
            )}
          >
            {children as React.ReactNode}
          </Box>
        </div>
        {!hideFooter && (
          <>
            <Divider color="#DEDEDE" />
            <Box className={classNames(classes.footer, footerClass)}>
              {negativeText && onSecondaryAction && (
                <Button
                  fullWidth
                  size="medium"
                  className={classes.negativeCTA}
                  variant="text"
                  onClick={onSecondaryAction}
                >
                  {negativeText}
                </Button>
              )}
              {positiveText && onConfirm && (
                <Button
                  fullWidth
                  disabled={disablePositive}
                  size="medium"
                  onClick={onConfirm}
                >
                  {positiveText}
                </Button>
              )}
            </Box>
          </>
        )}
        {showLeftChildren && (
          <Box className={classes.leftChildContainer}>{leftChildren}</Box>
        )}
      </MuiDrawer>
    </StyledEngineProvider>
  );
}
export default forwardRef(Drawer);
